import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import get from 'lodash/get';
import isNull from 'lodash/isNull';
import isUndefined from 'lodash/isUndefined';
import isPlainObject from 'lodash/isPlainObject';
import isArray from 'lodash/isArray';
import differenceBy from 'lodash/differenceBy';
import isEqual from 'lodash/isEqual';
import size from 'lodash/size';
import { useIntl } from 'react-intl';
import cn from 'classnames';

import DiscardModal from '../common/DiscardModal';
import { OverrideReactSelectStyles } from '../../utils/select/override-react-select-styles';
import styles from './index.styl';

// allow true/false values to be passed
const checkForPresence = value => !isNull(value) && !isUndefined(value);

// TODO: need to check behavior for multi select with objects, adjust options to be {label, value} shape only
// TODO: it should be one source of truth for selected value, which comes from props

const SelectCustom = ({
  className,
  id,
  label,
  multi,
  required,
  name,
  options,
  placeholder,
  value,
  defaultValue,
  onChange,
  onBlur,
  searchable,
  needUpdateDefaultValue,
  backspaceRemoves,
  deleteRemoves,
  onCheckBeforeDelete,
  disabled,
  readOnly,
  canSearch,
  onInputChange,
  isLoading,
  virtualized,
  onMenuScrollToBottom,
  ...other
}) => {
  const { formatMessage } = useIntl();

  const [selected, setSelected] = useState(null);
  const [checkValue, setCheckValue] = useState(null);

  const getSelectedValue = useCallback(
    (propsValue, propsDefaultValue) => {
      // simple hack, that should be removed after fixing TODO above
      if (!propsValue && !propsDefaultValue) {
        setSelected(multi ? [] : null);
        return;
      }

      const valueToUse = checkForPresence(propsDefaultValue)
        ? propsDefaultValue
        : propsValue;

      if (needUpdateDefaultValue && isPlainObject(valueToUse)) {
        setSelected(valueToUse);
        return;
      }

      if (checkForPresence(valueToUse)) {
        if (multi && isArray(valueToUse)) {
          const selectedValue = valueToUse.map(item => {
            if (isPlainObject(item)) {
              if (Object.hasOwn(item, 'name')) {
                return { value: item, label: item.name };
              }

              return item;
            }
            const labelText = get(
              options.filter(option => option.value === item),
              '[0].label',
            );
            return { value: item, label: labelText || item };
          });
          setSelected(selectedValue);
        } else {
          const labelText = get(
            options.filter(option => option.value === valueToUse),
            '[0].label',
          );

          setSelected({ value: valueToUse, label: labelText || valueToUse });
        }
      }
    },
    [multi, options, needUpdateDefaultValue],
  );

  useEffect(() => {
    getSelectedValue(value, defaultValue);
  }, [value, defaultValue]);

  const getFilteredOptions = useCallback((val = [], opts = []) => {
    return opts.filter(optionsItem => {
      if (isArray(val)) {
        return !val.find(item => {
          if (isPlainObject(optionsItem.value)) {
            return optionsItem.value.id === item.value.id;
          }
          return optionsItem.value === item.value;
        });
      }
      return true;
    });
  }, []);

  const handleChange = useCallback(
    newValue => {
      if (onCheckBeforeDelete && size(selected) > size(newValue)) {
        const difference = get(differenceBy(selected, newValue), '0', null);
        if (onCheckBeforeDelete(difference)) {
          setCheckValue(difference);
          return;
        }
      }

      setSelected(newValue);

      if (onChange) {
        if (multi && !newValue) {
          onChange([]);
          return;
        }

        onChange(newValue);
      }
    },
    [onChange, onCheckBeforeDelete, selected, multi],
  );

  const handleModalSubmit = useCallback(() => {
    const updatedSelection = selected.filter(
      item => !isEqual(checkValue, item),
    );
    setSelected(updatedSelection);
    setCheckValue(null);
    if (onChange) onChange(updatedSelection);
  }, [selected, checkValue, onChange]);

  const handleModalCancel = useCallback(() => {
    setCheckValue(null);
  }, []);

  const filteredOptions = useMemo(() => {
    const filterSelectOptions = selectData =>
      selectData.filter(el => el.enabled);

    const optionsHaveEnabledAttribute =
      options && options.some(item => item.enabled);

    const updatedOptions = optionsHaveEnabledAttribute
      ? filterSelectOptions(options)
      : options;

    return getFilteredOptions(selected, updatedOptions).map(option => {
      return option.label.length > 110
        ? { ...option, label: `${option.value.name.slice(0, 110)}...` }
        : option;
    });
  }, [options, selected]);

  return (
    <div
      id={id}
      {...other}
      className={cn(
        styles.root,
        {
          [styles['root--readOnly']]: readOnly,
        },
        className,
      )}
    >
      {label && (
        <label className={cn(styles.label, { [styles.required]: required })}>
          {label}
        </label>
      )}
      <Select
        {...other}
        menuPlacement="auto"
        isMulti={multi}
        options={filteredOptions}
        placeholder={placeholder}
        onBlur={onBlur}
        searchable={searchable}
        isDisabled={disabled}
        clearable={false}
        name={name || 'form-field-name'}
        onChange={handleChange}
        value={selected}
        autoBlur
        inputProps={{ readOnly: !(canSearch || multi) }}
        defaultValue={defaultValue}
        backspaceRemoves={backspaceRemoves}
        deleteRemoves={deleteRemoves}
        onInputChange={onInputChange}
        isLoading={isLoading}
        onMenuScrollToBottom={onMenuScrollToBottom}
        styles={OverrideReactSelectStyles}
      />
      {multi && Boolean(checkValue) && (
        <DiscardModal
          onSubmit={handleModalSubmit}
          onCancel={handleModalCancel}
          isOpened={Boolean(checkValue)}
          title={formatMessage({ id: 'profile.fields.removeItem' })}
        />
      )}
    </div>
  );
};

SelectCustom.propTypes = {
  className: PropTypes.string,
  id: PropTypes.string,
  label: PropTypes.string,
  multi: PropTypes.bool,
  required: PropTypes.bool,
  name: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.object),
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
    PropTypes.array,
    PropTypes.object,
  ]),
  defaultValue: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
    PropTypes.array,
    PropTypes.object,
  ]),
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  searchable: PropTypes.bool,
  needUpdateDefaultValue: PropTypes.bool,
  backspaceRemoves: PropTypes.bool,
  deleteRemoves: PropTypes.bool,
  onCheckBeforeDelete: PropTypes.func,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  canSearch: PropTypes.bool,
  onInputChange: PropTypes.func,
  isLoading: PropTypes.bool,
  virtualized: PropTypes.bool,
  onMenuScrollToBottom: PropTypes.func,
};

export default SelectCustom;



// WEBPACK FOOTER //
// ./src/components/SelectCustom/index.jsx